import shortid from 'shortid';
import { EventEmitter } from 'events';
import Sentry from '@saucelabs/sl-util-sentry';

import constants from './constants';
import actions from './actions';
import utils from './utils';

const PROTOCOL_VERSION = '1.0.0';
const {
  CHECK_CHANNEL_STATE_INTERVAL_MS,
  MAX_RECONNECTION_TIMEOUT_MS,
  MAX_TIME_TO_WAIT_FOR_CHANNEL_MS,
  MAX_TIME_TO_WAIT_FOR_SEND_MESSAGE_RESPONSE_MS,
  RECONNECTION_RETRY_INTITIAL_TIMEOUT_MS,
} = constants;
const {
  ERROR,
  SCREENSHOT_RETRIEVED,
  SESSION_CLOSED,
  SESSION_WILL_EXPIRE,
  STATUS_RECEIVED,
} = constants.MESSAGE_NAMES_INCOMING;
const {
  CLOSE_SESSION,
  GET_STATUS,
  TAKE_SCREENSHOT,
} = constants.MESSAGE_NAMES_OUTGOING;
const {
  IN_PROGRESS,
  NEW,
  QUEUED,
} = constants.SOCKET_STATES;
const CLOSE_SESSION_FROM_ELSEWHERE = 'close_session_from_elsewhere';

const messageEvents = new EventEmitter();
let socket;
let reconnectionRetryCount = 0;
let maxRetryTimer;
let pendingRetryTimer;

/**
 * Returns whether or not the socket is open
 * @returns {boolean}
 */
const isOpen = () => socket?.readyState === WebSocket.OPEN;

/**
 * Returns whether the channel state is either NEW or QUEUED
 * @returns {boolean}
 */
const isChannelNewOrQueued = (message = {}) => [NEW, QUEUED].includes(message.status);

/**
 * Enrich passed in properties with default ones and send it to the socket
 * @param {string} messageName - the name of the outgoing message
 * @throws {ReferenceError} if no socket is open to send a message to
 * @throws {Error} if the max time waiting for a response to a sent message is exeeded
 * @returns {Object} payload of the incoming message (https://gitlab.tools.saucelabs.net/live/live-session-manager#web-socket-communication-protocol)
 */
const sendMessage = async (messageName) => {
  if (!isOpen()) {
    throw new ReferenceError('There is no LSM socket established to send a message to.');
  }

  const messageId = shortid.generate();
  const payload = {
    id: messageId,
    version: PROTOCOL_VERSION,
    name: messageName,
  };

  return new Promise((resolve, reject) => {
    const maxWaitForResponse = setTimeout(() => {
      reject(new Error(`No response retrieved for ${messageName} with id "${messageId}"`));
    }, MAX_TIME_TO_WAIT_FOR_SEND_MESSAGE_RESPONSE_MS);

    // clear waiting for response timer when a session was closed to not have that hanging around
    const onSessionCloseRequest = () => {
      clearTimeout(maxWaitForResponse);
      messageEvents.removeListener(SESSION_CLOSED, onSessionCloseRequest);
    };
    messageEvents.addListener(SESSION_CLOSED, onSessionCloseRequest);

    const onMessageReceived = (message) => {
      if (message.id !== messageId) {
        return;
      }

      if (message.name === ERROR) {
        const fallback = 'Got into unexpected troubles while interacting with your live session';
        reject(new Error(message.payload?.message ?? fallback));
      } else {
        resolve(message.payload);
      }

      clearTimeout(maxWaitForResponse);
      messageEvents.removeListener(messageId, onMessageReceived);
      messageEvents.removeListener(SESSION_CLOSED, onSessionCloseRequest);
    };
    messageEvents.addListener(messageId, onMessageReceived);

    socket.send(JSON.stringify(payload));
  });
};

/**
 * Sends GET_STATUS to socket every CHECK_CHANNEL_STATE_INTERVAL_MS
 * unless the channel status is not NEW or QUEUED anymore
 * Stops after MAX_TIME_TO_WAIT_FOR_CHANNEL and fails
 * @throws {Error} timout if max waiting time for a connected channel exceeds
 * @returns {Object} payload of a status message
 */
const waitForEstablishedChannel = async () => {
  let retryInterval = null;
  let retryTimeout = null;

  try {
    return await new Promise((resolve, reject) => {
      retryTimeout = setTimeout(() => {
        reject(new Error('Max time trying to connect to LSM channel exceeded'));
      }, MAX_TIME_TO_WAIT_FOR_CHANNEL_MS);

      retryInterval = setInterval(async () => {
        let message;

        try {
          message = await sendMessage(GET_STATUS);
        } catch (error) {
          reject(error);
        }

        if (!isChannelNewOrQueued(message)) {
          resolve(message);
        }
      }, CHECK_CHANNEL_STATE_INTERVAL_MS);
    });
  } finally {
    clearTimeout(retryTimeout);
    clearInterval(retryInterval);
  }
};

/**
 * Handles logic based on incoming socket messages and emits corresponding messages
 * @param {string} payload - the data of an incoming socket message
 */
const handleMessageReceived = ({ data }) => {
  const message = JSON.parse(data);
  const { id } = message;

  switch (message.name) {
    case SCREENSHOT_RETRIEVED:
    case STATUS_RECEIVED:
      messageEvents.emit(id, message);
      break;

    case SESSION_WILL_EXPIRE:
      messageEvents.emit(SESSION_WILL_EXPIRE, message);
      break;

    case SESSION_CLOSED:
      // special case since the user flow/UI is different depending on whether
      // the "current tab" is the initiator of closing
      // or it's closed from somewhere else
      if (messageEvents.listeners(id).length > 0) {
        messageEvents.emit(id, message);
      } else {
        messageEvents.emit(CLOSE_SESSION_FROM_ELSEWHERE, message);
      }
      messageEvents.emit(SESSION_CLOSED, message);
      break;

    case ERROR:
      // special case since an error can occur
      // either as a result of a previous request (like get_screenshot)
      // or without any doing of the user
      if (messageEvents.listeners(id).length > 0) {
        messageEvents.emit(id, message);
      } else {
        Sentry.captureException(new Error(data));
      }
      break;

    default:
      break;
  }
};

/**
 * Close pending timers and resets globals to initial values
 * only used internally to clean up
 */
const teardown = () => {
  if (socket) {
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    socket.onopen = null;
    socket = null;
  }
  messageEvents.removeAllListeners();
};

/**
 * Stops the session
 * @returns {?Object} either the response of a close_session request or null
 */
const stopSession = async () => (
  isOpen() ? sendMessage(CLOSE_SESSION) : null
);

/**
 * Requests to take a screenshot
 * @returns {?Object}
 */
const takeScreenshot = async () => (
  isOpen() ? sendMessage(TAKE_SCREENSHOT) : null
);

/**
 * Try to reconnect exponetially to the socket/channel if session was not closed by purpose
 * Stops trying after MAX_RECONNECTION_TIMEOUT_MS and dispatches reconnection failure
 * @param {function} createSocket will be called to create the socket with all listeners
 * @param {function} dispatch redux store dispatch method
 */
const reconnectChannel = async (createSocket, dispatch) => {
  dispatch(actions.reconnectionRequest());

  if (reconnectionRetryCount === 0) {
    maxRetryTimer = setTimeout(() => {
      clearTimeout(pendingRetryTimer);
      dispatch(actions.reconnectionFailure());
      dispatch(actions.sessionIsClosed());
      messageEvents.emit(CLOSE_SESSION_FROM_ELSEWHERE);
    }, MAX_RECONNECTION_TIMEOUT_MS);
  }

  reconnectionRetryCount += 1;

  pendingRetryTimer = setTimeout(async () => {
    let internalSocket;

    try {
      internalSocket = await createSocket();
    } catch (error) {
      return; // ignore the error
    }

    let channelStatus;
    try {
      channelStatus = await sendMessage(GET_STATUS);
    } catch (error) {
      internalSocket.close();
      return;
    }

    if (channelStatus?.status === IN_PROGRESS) {
      clearTimeout(maxRetryTimer);
      dispatch(actions.reconnectionSuccess());
    } else {
      dispatch(actions.reconnectionFailure());
      messageEvents.emit(CLOSE_SESSION_FROM_ELSEWHERE);
      internalSocket.close();
    }
  }, RECONNECTION_RETRY_INTITIAL_TIMEOUT_MS * (2 ** reconnectionRetryCount));
};

/**
 * Creates and returns the socket to the LiveSessionManager
 * only used internally
 * @param {string} sessionId
 * @param {function} dispatch redux store dispatch method
 * @throws {DOMException} if the WebSocket can not be created
 * @returns {WebSocket} the websocket instance to LiveSessionManager or an Error
 */
const createSocketForSession = async (sessionId, dispatch) => {
  if (isOpen()) {
    return socket;
  }

  let sessionClosedProperly = false;
  let socketHasError = false;

  socket = await new Promise((resolve, reject) => {
    let tempSocket;

    try {
      tempSocket = new WebSocket(utils.createSocketUrl(sessionId));
      tempSocket.onerror = (error) => {
        socketHasError = true;
        stopSession();
        reject(error);
      };
      tempSocket.onclose = () => {
        teardown();

        if (!sessionClosedProperly && !socketHasError) {
          reconnectChannel(() => createSocketForSession(sessionId, dispatch), dispatch);
        }
      };
      tempSocket.onmessage = handleMessageReceived;
      tempSocket.onopen = () => resolve(tempSocket);
    } catch (error) {
      reject(error);
    }
  });

  const onSessionWillExpire = (sessionWillExpireMessage) => {
    dispatch(actions.secondsLeftUpdate(sessionWillExpireMessage.payload.seconds_left));
    if (sessionWillExpireMessage.payload.seconds_left <= 0) {
      messageEvents.off(SESSION_WILL_EXPIRE, onSessionWillExpire);
    }
  };
  messageEvents.on(SESSION_WILL_EXPIRE, onSessionWillExpire);

  // set flag to indicate a graceful close to NOT start reconnecting
  [SESSION_CLOSED, CLOSE_SESSION_FROM_ELSEWHERE].forEach((eventName) => {
    messageEvents.once(eventName, () => { sessionClosedProperly = true; });
  });

  // special case if the session was closed by someone/something else
  // just inform the store about it
  const onCloseSessionFromElsewhere = (closeSessionMessage = {}) => {
    dispatch(actions.sessionIsClosed(closeSessionMessage.payload));
    messageEvents.off(CLOSE_SESSION_FROM_ELSEWHERE, onCloseSessionFromElsewhere);
  };
  messageEvents.on(CLOSE_SESSION_FROM_ELSEWHERE, onCloseSessionFromElsewhere);

  return socket;
};

/**
 * Main entry point
 * Creates a socket, connects to the channel and asks for the status
 * @param {string} sessionId
 * @param {function} dispatch redux store dispatch method
 * @throws {Error} any error while creating a socket or waiting until the channel is in progress
 * @returns {Object} the status of an in_progress channel (https://gitlab.tools.saucelabs.net/live/live-session-manager#web-socket-communication-protocol)
 */
const connectChannel = async (sessionId, dispatch) => {
  reconnectionRetryCount = 0;
  maxRetryTimer = null;

  await createSocketForSession(sessionId, dispatch);

  let channelStatus = await sendMessage(GET_STATUS);

  if (isChannelNewOrQueued(channelStatus)) {
    channelStatus = await waitForEstablishedChannel();
  }

  if (channelStatus?.status === IN_PROGRESS) {
    return channelStatus;
  }

  const channelError = new Error(`Channel is not ready (status: ${channelStatus?.status})`);
  channelError.channelStatus = channelStatus?.status;
  throw channelError;
};

export default {
  connectChannel,
  PROTOCOL_VERSION,
  stopSession,
  takeScreenshot,
};
